import dayjs from "dayjs";
import { parseCalendarDate } from "./dateOnly";

export const CLOSED_TASK_STATUSES = new Set([
  "Fixed",
  "Closed",
  "Not Reproducible",
  "Cannot Reproduce",
  "Invalid",
  "Won't Fix",
  "Duplicate",
]);

export const NOTEBOOK_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "in_review", label: "In Review" },
  { value: "blocked", label: "Blocked" },
  { value: "done", label: "Done" },
  { value: "overdue", label: "Overdue" },
];

export function isTaskClosed(status) {
  return CLOSED_TASK_STATUSES.has(status);
}

/** Open = not Done/closed and not Blocked (includes Open, In Progress, In Review, etc.). */
export function isNotebookTaskOpen(status) {
  if (status === "Blocked") return false;
  return !isTaskClosed(status);
}

/** A line's heading tasks plus its sub-tasks: everything that has its own status. */
export function entryAllTasks(entry) {
  return [...(entry.tasks || []), ...(entry.subtasks || [])];
}

/**
 * Fold a line's sub-tasks back into bullets. Each bullet typed in the notebook
 * becomes one Task per owner, all sharing a `position`; a task added later from
 * the Story modal has no position and stands alone.
 */
export function groupSubtasks(subtasks = []) {
  const groups = [];
  const byKey = new Map();
  for (const task of subtasks) {
    const key = task.position != null ? `pos-${task.position}` : `issue-${task.issue_id}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, title: task.title, depth: task.depth || 0, tasks: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.tasks.push(task);
  }
  return groups;
}

export function countOpenNotebookTasks(entries = []) {
  let count = 0;
  for (const entry of entries) {
    for (const task of entryAllTasks(entry)) {
      if (isNotebookTaskOpen(task.status)) count += 1;
    }
  }
  return count;
}

/** Effective ETA for a notebook row — prefers linked task due dates over entry snapshot. */
export function getEntryDueDate(entry) {
  const tasks = entryAllTasks(entry);
  const datedTasks = tasks.filter((t) => t.due_date);
  if (datedTasks.length > 0) {
    const openDated = datedTasks.filter((t) => isNotebookTaskOpen(t.status));
    const pool = openDated.length > 0 ? openDated : datedTasks;
    return pool
      .map((t) => String(t.due_date).slice(0, 10))
      .sort()[0];
  }
  return entry.due_date ? String(entry.due_date).slice(0, 10) : null;
}

/** Map stored status to dropdown value (Tasks use Closed for done). */
export function taskStatusSelectValue(status) {
  if (isTaskClosed(status)) return "Closed";
  return status || "Open";
}

/** Flat nav order for Daily Notebook detail modal: story then its tasks, matching list UI. */
export function buildNotebookNavIds(groupedEntries = []) {
  const ids = [];
  for (const [, entries] of groupedEntries) {
    for (const entry of entries) {
      const tasks = entry.tasks || [];
      const storyId = entry.story_id ? Number(entry.story_id) : null;
      if (storyId) {
        ids.push(String(storyId));
        for (const t of [...tasks, ...(entry.subtasks || [])]) {
          if (t.issue_id != null) ids.push(String(t.issue_id));
        }
      } else if (tasks.length === 1) {
        ids.push(String(tasks[0].issue_id));
      } else {
        for (const t of tasks) {
          if (t.issue_id != null) ids.push(String(t.issue_id));
        }
      }
    }
  }
  return ids;
}

/** Mark all tasks under a notebook story row as done in cached entries. */
export function patchNotebookStoryChildrenDone(cached, storyId) {
  if (!cached?.entries || storyId == null) return cached;
  const sid = Number(storyId);
  return {
    ...cached,
    entries: cached.entries.map((entry) => {
      if (Number(entry.story_id) !== sid) return entry;
      return {
        ...entry,
        tasks: (entry.tasks || []).map((t) =>
          isTaskClosed(t.status) ? t : { ...t, status: "Closed" }
        ),
        subtasks: (entry.subtasks || []).map((t) =>
          isTaskClosed(t.status) ? t : { ...t, status: "Closed" }
        ),
      };
    }),
  };
}

/** A notebook line's own sprint, patched after moving its Story to/from backlog. */
export function patchNotebookEntrySprint(cached, storyId, sprintId) {
  if (!cached?.entries || storyId == null) return cached;
  const sid = Number(storyId);
  return {
    ...cached,
    entries: cached.entries.map((entry) =>
      Number(entry.story_id) === sid ? { ...entry, sprint_id: sprintId } : entry
    ),
  };
}

export function taskMatchesStatusFilter(status, filter) {
  if (filter === "all") return true;
  if (filter === "open") return status === "Open";
  if (filter === "in_progress") return status === "In Progress";
  if (filter === "in_review") return status === "Testing";
  if (filter === "blocked") return status === "Blocked";
  if (filter === "done") return isTaskClosed(status);
  return true;
}

export function entryMatchesFilters(entry, { statusFilter, dateFrom, dateTo }) {
  const logDate = parseCalendarDate(entry.log_date);
  if (dateFrom && logDate.isBefore(parseCalendarDate(dateFrom), "day")) return false;
  if (dateTo && logDate.isAfter(parseCalendarDate(dateTo), "day")) return false;

  const tasks = entryAllTasks(entry);
  if (statusFilter === "all") return true;
  if (statusFilter === "overdue") {
    const effectiveDue = getEntryDueDate(entry);
    if (!effectiveDue) return false;
    const overdue = parseCalendarDate(effectiveDue).isBefore(dayjs().startOf("day"), "day");
    return overdue && tasks.some((t) => !isTaskClosed(t.status));
  }
  if (tasks.length === 0) return statusFilter === "all";
  return tasks.some((t) => taskMatchesStatusFilter(t.status, statusFilter));
}

export function getDueRemainingLabel(dueDate, tasks = []) {
  if (!dueDate) return null;
  const openTasks = (tasks || []).filter((t) => !isTaskClosed(t.status));
  if (openTasks.length === 0) return null;

  const due = parseCalendarDate(dueDate).startOf("day");
  const today = dayjs().startOf("day");
  const diff = due.diff(today, "day");

  if (diff < 0) {
    const n = Math.abs(diff);
    return n === 1 ? "1 day overdue" : `${n} days overdue`;
  }
  if (diff === 0) return "Due today";
  if (diff === 1) return "1 day left";
  return `${diff} days left`;
}

/** Accent for notebook row — most urgent open task status or overdue/done. */
export function entryAccentKey(entry) {
  const tasks = entryAllTasks(entry);
  const effectiveDue = getEntryDueDate(entry);
  if (tasks.length === 0) return "note";

  if (tasks.every((t) => isTaskClosed(t.status))) return "done";

  if (
    effectiveDue &&
    parseCalendarDate(effectiveDue).isBefore(dayjs().startOf("day"), "day") &&
    tasks.some((t) => !isTaskClosed(t.status))
  ) {
    return "overdue";
  }

  const priority = ["Blocked", "In Progress", "Testing", "Open"];
  for (const status of priority) {
    if (tasks.some((t) => t.status === status)) return status;
  }
  return "Open";
}

export function accentBorderColor(key) {
  const map = {
    note: "#d6d3d1",
    overdue: "#dc2626",
    Blocked: "#dc2626",
    "In Progress": "#1272B3",
    Testing: "#7c3aed",
    Open: "#64748b",
    done: "#059669",
  };
  return map[key] || map.Open;
}

/** Days from entry/log date to ETA (due_date). Returns null if invalid or negative. */
export function etaDaysBetween(entryDate, etaDate) {
  if (!entryDate || !etaDate) return null;
  const diff = parseCalendarDate(etaDate)
    .startOf("day")
    .diff(parseCalendarDate(entryDate).startOf("day"), "day");
  return diff >= 0 ? diff : null;
}

export function etaDateFromEntryAndDays(entryDate, days) {
  const n = Number(days);
  if (entryDate == null || Number.isNaN(n) || n < 0) return null;
  return parseCalendarDate(entryDate).add(n, "day");
}
